import { FormEvent, useEffect, useState } from 'react';
import { adminApi } from '@/services/api';
import { formatDateTime, formatNumber } from '@/services/format';
import type { CreditEntry } from '@/types';

const cycleLabel = (c: CreditEntry['cycleType']) => {
  if (typeof c === 'string') return c;
  return ['Sem ciclo', 'Diário', 'Semanal', 'Mensal', 'Anual'][c] ?? String(c);
};

// Numeric values mirror UsageCycleType on the server (None=0, Daily=1, ..., Yearly=4).
const CYCLE_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Sem ciclo' },
  { value: 1, label: 'Diário' },
  { value: 2, label: 'Semanal' },
  { value: 3, label: 'Mensal' },
  { value: 4, label: 'Anual' },
];

export function CreditsPage() {
  const [items, setItems] = useState<CreditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientId, setClientId] = useState('');
  const [featureToken, setFeatureToken] = useState('');

  // "Add credits" modal state — local to this page, no global store needed.
  const [addOpen, setAddOpen] = useState(false);
  const [addClientId, setAddClientId] = useState('');
  const [addFeatureToken, setAddFeatureToken] = useState('');
  const [addAmount, setAddAmount] = useState('');
  const [addCycleType, setAddCycleType] = useState<number>(0);
  const [addCreditsPerCycle, setAddCreditsPerCycle] = useState('');
  const [addCycleStart, setAddCycleStart] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const cid = clientId ? Number(clientId) : undefined;
      const ft = featureToken.trim() || undefined;
      setItems(await adminApi.credits(cid, ft));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar os créditos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = (e: FormEvent) => { e.preventDefault(); load(); };

  const resetAddForm = () => {
    setAddClientId(''); setAddFeatureToken(''); setAddAmount('');
    setAddCycleType(0); setAddCreditsPerCycle(''); setAddCycleStart('');
    setAddError(null);
  };

  const onAddSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setAddBusy(true);
    setAddError(null);
    try {
      const cid = Number(addClientId);
      const amt = Number(addAmount);
      if (!Number.isFinite(cid) || cid <= 0) throw new Error('ID do cliente inválido');
      if (!addFeatureToken.trim()) throw new Error('Token da funcionalidade obrigatório');
      if (!Number.isFinite(amt) || amt <= 0) throw new Error('Quantidade tem de ser maior que zero');

      await adminApi.addCredits({
        clientId: cid,
        featureToken: addFeatureToken.trim(),
        amount: amt,
        cycleType: addCycleType,
        creditsPerCycle: addCreditsPerCycle ? Number(addCreditsPerCycle) : undefined,
        cycleStart: addCycleStart || undefined,
      });
      setAddOpen(false);
      resetAddForm();
      await load();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Não foi possível adicionar créditos');
    } finally {
      setAddBusy(false);
    }
  };

  return (
    <div className="p-8 max-w-[1280px] mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="page-h1">Saldos de crédito</h1>
        {/* Irreversible "create" per the design system's 3-intent button matrix — once granted,
            credits are on the customer's ledger and only a separate destructive action would
            remove them. That's the magni-blue lane. */}
        <button
          onClick={() => { resetAddForm(); setAddOpen(true); }}
          className="px-4 py-2 bg-magni-blue text-white hover:bg-magni-blue-700 rounded-md text-sm font-semibold transition-colors"
        >Adicionar créditos</button>
      </div>

      {/* Apply is an idempotent read-only refresh — magni-green outline per design intent matrix. */}
      <form onSubmit={onSubmit} className="card mb-6 flex gap-4 items-end flex-wrap">
        <label className="flex flex-col text-xs font-semibold text-gray-700">
          ID do cliente
          <input
            type="number"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="mt-1.5 px-3 py-2 border border-gray-200 rounded-sm text-sm w-40"
            placeholder="qualquer"
          />
        </label>
        <label className="flex flex-col text-xs font-semibold text-gray-700">
          Token da funcionalidade
          <input
            value={featureToken}
            onChange={(e) => setFeatureToken(e.target.value)}
            className="mt-1.5 px-3 py-2 border border-gray-200 rounded-sm text-sm w-64"
            placeholder="qualquer"
          />
        </label>
        <button
          type="submit"
          className="px-4 py-2 border border-magni-green-700 text-magni-green-700 hover:bg-magni-green-50 rounded-md text-sm font-semibold transition-colors"
        >Aplicar</button>
      </form>

      {loading && <p className="text-gray-500">A carregar…</p>}
      {error && <p className="text-magni-red-600">{error}</p>}
      {!loading && !error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-left">
              <tr>
                <th className="px-6 py-3 font-semibold">Cliente</th>
                <th className="px-6 py-3 font-semibold">Funcionalidade</th>
                <th className="px-6 py-3 font-semibold text-right">Disponível</th>
                <th className="px-6 py-3 font-semibold text-right">Por ciclo</th>
                <th className="px-6 py-3 font-semibold">Ciclo</th>
                <th className="px-6 py-3 font-semibold">Próximo reset</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-gray-100">
                  <td className="px-6 py-3 font-mono text-gray-800">{c.clientId}</td>
                  <td className="px-6 py-3 font-mono text-xs text-gray-700">{c.featureToken}</td>
                  <td className="px-6 py-3 text-right text-gray-800">{formatNumber(c.availableCredits)}</td>
                  <td className="px-6 py-3 text-right text-gray-800">{c.creditsPerCycle != null ? formatNumber(c.creditsPerCycle) : '—'}</td>
                  <td className="px-6 py-3 text-gray-800">{cycleLabel(c.cycleType)}</td>
                  <td className="px-6 py-3 text-xs text-gray-700">{formatDateTime(c.cycleResetAt)}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-6 text-center text-gray-500">Sem saldos de crédito.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {addOpen && (
        <div
          // Modal overlay alpha matches the design system: rgba(--gray-900, 0.5), no blur.
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ backgroundColor: 'rgba(20, 22, 26, 0.5)' }}
          onClick={() => !addBusy && setAddOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold text-gray-900 mb-4">Adicionar créditos</h2>
            <form onSubmit={onAddSubmit} className="flex flex-col gap-3">
              <label className="flex flex-col text-xs font-semibold text-gray-700">
                ID do cliente *
                <input type="number" required value={addClientId} disabled={addBusy}
                  onChange={(e) => setAddClientId(e.target.value)}
                  className="mt-1.5 px-3 py-2 border border-gray-200 rounded-sm text-sm" />
              </label>
              <label className="flex flex-col text-xs font-semibold text-gray-700">
                Token da funcionalidade *
                <input required value={addFeatureToken} disabled={addBusy}
                  onChange={(e) => setAddFeatureToken(e.target.value)}
                  placeholder="ex: FinancialManagement"
                  className="mt-1.5 px-3 py-2 border border-gray-200 rounded-sm text-sm font-mono" />
              </label>
              <label className="flex flex-col text-xs font-semibold text-gray-700">
                Quantidade *
                <input type="number" step="any" required value={addAmount} disabled={addBusy}
                  onChange={(e) => setAddAmount(e.target.value)}
                  className="mt-1.5 px-3 py-2 border border-gray-200 rounded-sm text-sm" />
              </label>
              <label className="flex flex-col text-xs font-semibold text-gray-700">
                Ciclo
                <select value={addCycleType} disabled={addBusy}
                  onChange={(e) => setAddCycleType(Number(e.target.value))}
                  className="mt-1.5 px-3 py-2 border border-gray-200 rounded-sm text-sm bg-white">
                  {CYCLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              {addCycleType > 0 && (
                <>
                  <label className="flex flex-col text-xs font-semibold text-gray-700">
                    Créditos por ciclo (opcional)
                    <input type="number" step="any" value={addCreditsPerCycle} disabled={addBusy}
                      onChange={(e) => setAddCreditsPerCycle(e.target.value)}
                      placeholder="se omitido, mantém o atual"
                      className="mt-1.5 px-3 py-2 border border-gray-200 rounded-sm text-sm" />
                  </label>
                  <label className="flex flex-col text-xs font-semibold text-gray-700">
                    Início do ciclo (opcional)
                    <input type="date" value={addCycleStart} disabled={addBusy}
                      onChange={(e) => setAddCycleStart(e.target.value)}
                      className="mt-1.5 px-3 py-2 border border-gray-200 rounded-sm text-sm" />
                  </label>
                </>
              )}
              {addError && <p className="text-magni-red-600 text-sm">{addError}</p>}
              {/* Per the button matrix, secondary actions on a screen take the same hue as the
                  primary's lane (blue here) as an outline. Submit is the filled blue. */}
              <div className="flex justify-end gap-2 mt-2">
                <button type="button" disabled={addBusy} onClick={() => setAddOpen(false)}
                  className="px-4 py-2 border border-magni-blue text-magni-blue hover:bg-magni-blue-50 rounded-md text-sm font-semibold transition-colors disabled:opacity-50"
                >Cancelar</button>
                <button type="submit" disabled={addBusy}
                  className="px-4 py-2 bg-magni-blue text-white hover:bg-magni-blue-700 rounded-md text-sm font-semibold transition-colors disabled:opacity-50"
                >{addBusy ? 'A guardar…' : 'Adicionar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
